import { useState } from 'react';
import { transformPluginsApi, TransformPluginRow } from './api/transform-plugins-client';
import '../admin.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FormFieldDef {
  key: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'lookup-table' | 'condition';
  options?: Array<{ value: string; label: string }>;
}

function toFormFieldDefs(raw: unknown): FormFieldDef[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (r): r is FormFieldDef =>
      typeof r === 'object' && r !== null && 'key' in r && 'label' in r && 'type' in r,
  );
}

interface TestRunResult {
  output?: string;
  error?: string;
  trace: Array<{ step: number; op: string; input: unknown; output?: unknown; error?: string }>;
}

// ---------------------------------------------------------------------------
// TestPanel
// ---------------------------------------------------------------------------

export interface TestPanelProps {
  plugin: TransformPluginRow;
}

export function TestPanel({ plugin }: TestPanelProps) {
  const formSchema = toFormFieldDefs(plugin.formSchema);
  const defaultConfig = (plugin.defaultConfig as Record<string, unknown>) ?? {};

  const [input, setInput] = useState('');
  // Config state: keyed by formSchema field key, seeded from defaultConfig
  const [config, setConfig] = useState<Record<string, unknown>>(() => {
    const seed: Record<string, unknown> = {};
    for (const f of formSchema) {
      seed[f.key] = defaultConfig[f.key] ?? '';
    }
    return seed;
  });
  const [result, setResult] = useState<TestRunResult | null>(null);
  const [running, setRunning] = useState(false);

  function setConfigField(key: string, value: unknown) {
    setConfig((prev) => ({ ...prev, [key]: value }));
  }

  async function runHandler() {
    setRunning(true);
    setResult(null);
    try {
      // Convert config values to strings for fields (Record<string, string>)
      const fields: Record<string, string> = {};
      const res = await transformPluginsApi.testRun(plugin.id, { input, fields, config });
      setResult(res);
    } catch (e) {
      setResult({
        error: e instanceof Error ? e.message : String(e),
        trace: [],
      });
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="rb-section">
      <h2 className="rb-section-title">Test Panel</h2>
      <p className="rb-hint">
        Run the plugin's expression against a test input. Runtime errors are shown inline.
      </p>

      {/* Input field */}
      <div className="rb-row">
        <label className="rb-label" htmlFor="test-panel-input">
          Input
        </label>
        <input
          id="test-panel-input"
          className="rb-input"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Enter test input value"
        />
      </div>

      {/* Config form — dynamically built from formSchema */}
      {formSchema.length > 0 && (
        <div>
          <p className="rb-hint" style={{ marginBottom: '8px' }}>
            Config (seeded from default values)
          </p>
          {formSchema.map((f) => (
            <div key={f.key} className="rb-row">
              <label className="rb-label" htmlFor={`config-${f.key}`}>
                {f.label}
              </label>
              {f.type === 'select' ? (
                <select
                  id={`config-${f.key}`}
                  className="rb-select"
                  value={String(config[f.key] ?? '')}
                  onChange={(e) => setConfigField(f.key, e.target.value)}
                >
                  {(f.options ?? []).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : f.type === 'number' ? (
                <input
                  id={`config-${f.key}`}
                  className="rb-input"
                  type="number"
                  value={String(config[f.key] ?? '')}
                  onChange={(e) => setConfigField(f.key, e.target.value)}
                />
              ) : (
                /* text | lookup-table | condition — all get a text input as v1 */
                <input
                  id={`config-${f.key}`}
                  className="rb-input"
                  type="text"
                  value={String(config[f.key] ?? '')}
                  onChange={(e) => setConfigField(f.key, e.target.value)}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Run button */}
      <div className="rb-row" style={{ marginTop: '8px' }}>
        <button
          type="button"
          onClick={runHandler}
          className="rb-btn rb-btn-save"
          disabled={running}
        >
          {running ? 'Running…' : 'Run'}
        </button>
      </div>

      {/* Output / error display */}
      {result && (
        <div style={{ marginTop: '16px' }}>
          {result.output !== undefined && (
            <div>
              <p className="rb-label">Output</p>
              <pre className="rb-section">{result.output}</pre>
            </div>
          )}
          {result.error !== undefined && (
            <pre className="rb-hint" style={{ color: '#dc2626' }}>
              {result.error}
            </pre>
          )}
          {result.trace.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              <p className="rb-hint">Trace</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {result.trace.map((t, i) => (
                  <li key={i} className="rb-hint">
                    Step {t.step} · {t.op} · {JSON.stringify(t.input)} →{' '}
                    {t.error ? (
                      <span style={{ color: '#dc2626' }}>{t.error}</span>
                    ) : (
                      JSON.stringify(t.output)
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
